// common/SafetyFormFilterMenu.js
// Filter menu component for safety form tabs (Near Miss, Safety Audit, Reporting)
// Adapts filter fields based on formType prop.

import { DelDatePicker } from "./DelDatePicker";
import {
  ProjectSelectionInput,
  SearchEmployeeInput,
  DelStatusSelectionInput,
} from "./FilterMenu";
import styles from "./FilterMenu.module.css";

/**
 * @param {String} formType - "near_miss" or "safety_audit"
 * @param {Object} filters - Current filter state
 * @param {Function} updateFilterAction - Callback to update a filter field: (partial) => void
 * @param {Function} onApplyFilters - Callback when Apply button clicked
 * @param {Function} onClearFilters - Callback when Clear button clicked
 * @param {Array} allEmployees - Employee list for dropdowns
 * @param {Array} allProjectInfo - For safety audit project filter
 * @param {Array} allInactiveProjects - For safety audit project filter (inactive)
 * @param {Array} statusOptions - Configurable status options per context
 */
export const SafetyFormFilterMenu = ({
  formType,
  filters,
  updateFilterAction,
  onApplyFilters,
  onClearFilters,
  allEmployees = [],
  allProjectInfo = [],
  allInactiveProjects = [],
  statusOptions = null,
}) => {
  const handleFilterChange = (field, value) => {
    updateFilterAction({ [field]: value });
  };

  const handleDatePickerDateChange = (field, date) => {
    if (!date) {
      handleFilterChange(field, "");
      return;
    }
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    handleFilterChange(field, `${year}-${month}-${day}`);
  };

  // Status handler
  const handleSetSelectedStatus = (status) => {
    if (!status) {
      handleFilterChange("status", "");
      return;
    }
    handleFilterChange("status", status.value);
  };

  // Created By handler
  const handleSetSelectedCreatedBy = (employee) => {
    if (!employee) {
      handleFilterChange("createdBy", "");
      handleFilterChange("createdByName", "");
      return;
    }
    handleFilterChange("createdBy", employee.value);
    handleFilterChange("createdByName", employee.label);
  };

  // Near Miss: Reported To
  const handleSetSelectedReportedTo = (employee) => {
    if (!employee) {
      handleFilterChange("reportedTo", "");
      handleFilterChange("reportedToName", "");
      return;
    }
    handleFilterChange("reportedTo", employee.value);
    handleFilterChange("reportedToName", employee.label);
  };

  // Near Miss: Reported By
  const handleSetSelectedReportedBy = (employee) => {
    if (!employee) {
      handleFilterChange("reportedBy", "");
      handleFilterChange("reportedByName", "");
      return;
    }
    handleFilterChange("reportedBy", employee.value);
    handleFilterChange("reportedByName", employee.label);
  };

  // Safety Audit: Supervisor
  const handleSetSelectedSupervisor = (employee) => {
    if (!employee) {
      handleFilterChange("supervisor", "");
      handleFilterChange("supervisorName", "");
      return;
    }
    handleFilterChange("supervisor", employee.value);
    handleFilterChange("supervisorName", employee.label);
  };

  // Safety Audit: Project
  const handleSetSelectedProject = (project) => {
    if (!project) {
      handleFilterChange("projectNumber", "");
      handleFilterChange("projectLabel", "");
      return;
    }
    handleFilterChange("projectNumber", project.value);
    handleFilterChange("projectLabel", project.label);
  };

  const defaultStatusOptions =
    formType === "near_miss"
      ? [
          { value: "", label: "All" },
          { value: "Draft", label: "Draft" },
          { value: "Submitted", label: "Submitted" },
        ]
      : [
          { value: "", label: "All" },
          { value: "Draft", label: "Draft" },
          { value: "Submitted", label: "Submitted" },
        ];

  const isNearMiss = formType === "near_miss";
  const isSafetyAudit = formType === "safety_audit";

  return (
    <div className={styles.reportFilterMenu}>
      <div className={styles.headerContainer}>
        <h3>Filter Options</h3>
      </div>
      <div className={styles.filtersContainer}>
        {/* Status */}
        <div className={styles.filterRow}>
          <label>Status:</label>
          <DelStatusSelectionInput
            selectedDelStatusValue={filters?.status}
            selectedDelStatusLabel={
              filters?.status
                ? (statusOptions || defaultStatusOptions).find(
                    (o) => o.value === filters.status
                  )?.label || filters.status
                : ""
            }
            setSelectedDelStatusFunction={handleSetSelectedStatus}
            options={statusOptions || defaultStatusOptions}
          />
        </div>

        {/* Created Date Range */}
        <div className={styles.filterRow}>
          <label>Created Date:</label>
          <div className={styles.datePickerContainer}>
            <div className={styles.datePickerGroup}>
              <label className={styles.dateLabel}>Start:</label>
              <DelDatePicker
                selected={filters?.createdStartDate || ""}
                onChange={(date) =>
                  handleDatePickerDateChange("createdStartDate", date)
                }
                selectsStart
                startDate={filters?.createdStartDate || ""}
                endDate={filters?.createdEndDate || ""}
                dateFormat="MM/dd/yyyy"
                placeholderText="mm/dd/yyyy"
                className={styles.datePicker}
                maxDate={filters?.createdEndDate || ""}
              />
            </div>
            <div className={styles.datePickerGroup}>
              <label className={styles.dateLabel}>End:</label>
              <DelDatePicker
                selected={filters?.createdEndDate || ""}
                onChange={(date) =>
                  handleDatePickerDateChange("createdEndDate", date)
                }
                selectsEnd
                startDate={filters?.createdStartDate || ""}
                endDate={filters?.createdEndDate || ""}
                minDate={filters?.createdStartDate || ""}
                dateFormat="MM/dd/yyyy"
                placeholderText="mm/dd/yyyy"
                className={styles.datePicker}
              />
            </div>
          </div>
        </div>

        {/* Near Miss: Incident Date Range */}
        {isNearMiss && (
          <div className={styles.filterRow}>
            <label>Incident Date:</label>
            <div className={styles.datePickerContainer}>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>Start:</label>
                <DelDatePicker
                  selected={filters?.incidentDateStart || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("incidentDateStart", date)
                  }
                  selectsStart
                  startDate={filters?.incidentDateStart || ""}
                  endDate={filters?.incidentDateEnd || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                  maxDate={filters?.incidentDateEnd || ""}
                />
              </div>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>End:</label>
                <DelDatePicker
                  selected={filters?.incidentDateEnd || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("incidentDateEnd", date)
                  }
                  selectsEnd
                  startDate={filters?.incidentDateStart || ""}
                  endDate={filters?.incidentDateEnd || ""}
                  minDate={filters?.incidentDateStart || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                />
              </div>
            </div>
          </div>
        )}

        {/* Safety Audit: Audit Date Range */}
        {isSafetyAudit && (
          <div className={styles.filterRow}>
            <label>Audit Date:</label>
            <div className={styles.datePickerContainer}>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>Start:</label>
                <DelDatePicker
                  selected={filters?.auditDateStart || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("auditDateStart", date)
                  }
                  selectsStart
                  startDate={filters?.auditDateStart || ""}
                  endDate={filters?.auditDateEnd || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                  maxDate={filters?.auditDateEnd || ""}
                />
              </div>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>End:</label>
                <DelDatePicker
                  selected={filters?.auditDateEnd || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("auditDateEnd", date)
                  }
                  selectsEnd
                  startDate={filters?.auditDateStart || ""}
                  endDate={filters?.auditDateEnd || ""}
                  minDate={filters?.auditDateStart || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                />
              </div>
            </div>
          </div>
        )}

        {/* Created By */}
        <div className={styles.filterRow}>
          <label>Created By:</label>
          <SearchEmployeeInput
            allEmployees={allEmployees}
            selectedEmployeeValue={filters?.createdBy}
            selectedEmployeeLabel={filters?.createdByName}
            setSelectedEmployeeFunction={handleSetSelectedCreatedBy}
            helpText="Search Employees"
          />
        </div>

        {/* Near Miss: Reported To */}
        {isNearMiss && (
          <div className={styles.filterRow}>
            <label>Reported To:</label>
            <SearchEmployeeInput
              allEmployees={allEmployees}
              selectedEmployeeValue={filters?.reportedTo}
              selectedEmployeeLabel={filters?.reportedToName}
              setSelectedEmployeeFunction={handleSetSelectedReportedTo}
              helpText="Search Employees"
            />
          </div>
        )}

        {/* Near Miss: Reported By */}
        {isNearMiss && (
          <div className={styles.filterRow}>
            <label>Reported By:</label>
            <SearchEmployeeInput
              allEmployees={allEmployees}
              selectedEmployeeValue={filters?.reportedBy}
              selectedEmployeeLabel={filters?.reportedByName}
              setSelectedEmployeeFunction={handleSetSelectedReportedBy}
              helpText="Search Employees"
            />
          </div>
        )}

        {/* Safety Audit: Project */}
        {isSafetyAudit && (
          <div className={styles.filterRow}>
            <ProjectSelectionInput
              allProjectInfo={allProjectInfo}
              allInactiveProjects={allInactiveProjects}
              selectedProjectValue={filters?.projectNumber}
              selectedProjectLabel={filters?.projectLabel}
              selectedProjectPhaseList={[]}
              setSelectedProjectFunction={handleSetSelectedProject}
            />
          </div>
        )}

        {/* Safety Audit: Supervisor */}
        {isSafetyAudit && (
          <div className={styles.filterRow}>
            <label>Supervisor:</label>
            <SearchEmployeeInput
              allEmployees={allEmployees}
              selectedEmployeeValue={filters?.supervisor}
              selectedEmployeeLabel={filters?.supervisorName}
              setSelectedEmployeeFunction={handleSetSelectedSupervisor}
              helpText="Search Employees"
            />
          </div>
        )}
      </div>
      <div className={styles.filterActions}>
        <button className={styles.clearButton} onClick={onClearFilters}>
          Clear
        </button>
        <button className={styles.applyButton} onClick={onApplyFilters}>
          Apply
        </button>
      </div>
    </div>
  );
};
